import { PlusCircleOutlined } from '@ant-design/icons';
import { Button, Input, message, Modal, Select, Spin } from 'antd';
import React, { FC, useState, useEffect } from 'react';
import TapdFieldDelete from '@/assets/images/public/tapd-field-delete.svg';
import { getUserId } from '@/infrastructure/auth';
import { getProductId } from '@/infrastructure/product-info';
import { storageSet } from '@/infrastructure/storage-utils';
import { getInitFields } from '@/presentation/components/tapd-info';
import {
  getCustomFields,
  getIssueTapdInfos,
  saveIssueTapdInfos,
} from '@/presentation/components/tapd-info/api';
import {
  CustomFieldsType,
  FieldsType,
  formatCustomFields,
  formatFieldsValue,
} from '@/presentation/components/tapd-info/create-tapd-modal';
import { RenderAutoComplete } from '@/presentation/components/tapd-info/create-tapd-modal/auto-complete';
import { RenderInput } from '@/presentation/components/tapd-info/create-tapd-modal/input';
import { RenderSelect } from '@/presentation/components/tapd-info/create-tapd-modal/select';
import { RenderUserSelect } from '@/presentation/components/tapd-info/create-tapd-modal/user-select';
import { getLocalTapd } from '@/presentation/components/tapd/create-tapd-modal';
import { Option } from '@/proto/field';
import { IBasicItemInfo, ITapdTemplateInfos } from '@/proto/manage';

export type EditType = 'add' | 'update';

interface IProps {
  visible: boolean;
  workspaceId: string;
  editType: EditType;
  mode: string;
  appVersionOptions: Option[],
  onHideEdit: () => void;
  openCreate: () => void;
}

export interface TapdInfoType {
  name: string;
  label: string;
  value: string;
}

export const formatTapdInfo = (tapdInfo: IBasicItemInfo[], customFields: CustomFieldsType[]) => {
  const newFields = tapdInfo.map((item: IBasicItemInfo) => {
    const findcustom = customFields.find((custom: CustomFieldsType) => custom.name === item.name);
    return ({
      name: item.name,
      value: item.value,
      label: item.label,
      type: findcustom?.type ?? '',
      options: findcustom?.options,
    });
  }).filter(item => item.name !== 'title_prefix');
  const newTitlePrefix = tapdInfo.find((item: IBasicItemInfo) => item.name === 'title_prefix')?.value;
  return {
    newFields,
    newTitlePrefix,
  };
};

export const EditTapdModal: FC<IProps> = (props) => {
  const { visible, workspaceId, editType, onHideEdit, openCreate } = props;
  const [loading, setLoading] = useState(false);
  const [fields, setFields] = useState<FieldsType[]>([]);
  const [customFields, setCustomFields] = useState<FieldsType[]>([]);
  const [tapdTitlePrefix, setTapdTitlePrefix] = useState('');
  const [mode, setMode] = useState<string>('');
  const [tapdTemplateInfos, setTapdTemplateInfos] = useState<ITapdTemplateInfos[]>([]);
  const initTitlePrefix = '【Bugly-性能监控】';

  const saveTapdInfo = async () => {
    if (!mode) {
      message.warning('请输入模板名称');
      return;
    }
    setLoading(true);
    const modeId = await saveIssueTapdInfos({
      productId: getProductId(),
      workSpaceId: workspaceId,
      operator: getUserId(),
      name: mode,
      issueTapdTemplateId: editType === 'update'
        ? tapdTemplateInfos.find(item => item.templateId === props.mode)?.templateId
        : undefined,
      data: [
        ...formatFieldsValue(fields),
        {
          name: 'title_prefix',
          value: tapdTitlePrefix,
          label: '标题前缀',
        },
      ],
    });
    storageSet(`${getProductId()}-tapd`, JSON.stringify({ ...getLocalTapd(), mode: modeId ?? getLocalTapd()?.mode }));
    setLoading(false);
  };

  const loadTapdFields = async () => {
    setLoading(true);
    const customFields = await getCustomFields(workspaceId);
    const newCustomFields = formatCustomFields(customFields, props.appVersionOptions);
    const tapdTemplateInfos = await getIssueTapdInfos({
      productId: getProductId(),
      workSpaceId: workspaceId,
    });
    setTapdTemplateInfos(tapdTemplateInfos);
    const tapdInfo = tapdTemplateInfos.find(item => item.templateId === props.mode) ?? tapdTemplateInfos[0];
    editType === 'update' ? setMode(tapdInfo?.templateName ?? '') : setMode('');
    const info = formatTapdInfo(tapdInfo?.data ?? [], newCustomFields);
    if (info.newTitlePrefix) {
      setTapdTitlePrefix(info.newTitlePrefix);
    } else {
      setTapdTitlePrefix(initTitlePrefix);
    }
    if (!!newCustomFields.length) setCustomFields(newCustomFields);
    if (!!tapdInfo?.data?.length && editType === 'update') {
      setFields(info.newFields);
    } else {
      setFields(getInitFields(props.appVersionOptions));
    }
    setLoading(false);
  };

  const getNewFields = (changeValue: string | number, name?: string | null) => {
    const newFields = fields.map((item: FieldsType) => {
      if (item.name === name) {
        return { ...item, value: changeValue };
      }
      return item;
    });
    setFields(newFields);
  };

  const renderFields = (fieldsItem: FieldsType, index: number) => {
    const options = customFields?.map((item: CustomFieldsType) => ({ label: item.label, value: item.label }));
    const renderItem = () => {
      switch (fieldsItem.type) {
        case 'select' :
          return <RenderSelect
            name={fieldsItem.name}
            label={'默认值'}
            options={fieldsItem.options!}
            getNewFields={getNewFields}
            value={fieldsItem.value}
            labelWidth={'60px'}
          />;
        case 'autoComplete':
          return <RenderAutoComplete
            name={fieldsItem.name}
            label={'默认值'}
            options={fieldsItem.options!}
            getNewFields={getNewFields}
            value={fieldsItem.value}
            labelWidth={'60px'}
          />;
        case 'userSelect':
          return <RenderUserSelect
            name={fieldsItem.name}
            label={'默认值'}
            getNewFields={getNewFields}
            value={fieldsItem.value as string}
            labelWidth={'60px'}
          />;
        default:
          return <RenderInput
            name={fieldsItem.name}
            label={'默认值'}
            value={fieldsItem.value}
            getNewFields={getNewFields}
            labelWidth={'60px'}
          />;
      }
    };
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div style={{ marginBottom: '10px', marginRight: '12px', display: 'flex' }}>
          <div style={{ lineHeight: '30px', marginRight: '12px', textAlign: 'right', width: '58px' }}>
            字段名
          </div>
          <Select
            placeholder={'请选择'}
            options={options}
            showSearch
            value={fieldsItem.label}
            onChange={(_, changeValue: any) => {
              const repeatFields = fields.find((item: FieldsType) => item.label === changeValue.label);
              if (repeatFields) {
                message.warning('字段名重复，请重新选择');
                setFields(fields);
                return;
              }
              const findField = customFields.find((item: CustomFieldsType) => item.label === changeValue.label);
              const newFields = fields.map((item) => {
                if (item.name === fieldsItem.name) {
                  return findField!;
                }
                return item;
              });
              setFields(newFields);
            }}
            style={{ width: '200px' }}
          />
        </div>
        {renderItem()}
        <img
          src={TapdFieldDelete}
          onClick={() => {
            const newFields = fields?.filter((_item: FieldsType, itemIndex: number) => itemIndex !== index);
            setFields(newFields);
          }}
          style={{
            height: '18px',
            marginTop: '6px',
            marginLeft: '10px',
            cursor: 'pointer',
          }}
          alt={''}
        />
      </div>
    );
  };

  useEffect(() => {
    if (visible) loadTapdFields();
  }, [visible]);

  return (
    <Modal
      title={<div style={{
        fontFamily: 'PingFang SC',
        fontWeight: 500,
        fontSize: '16px',
      }}>{editType === 'add' ? '创建新模板' : '编辑模板'}</div>}
      centered
      visible={visible}
      footer={null}
      onCancel={() => {
        setFields(fields);
        onHideEdit();
        openCreate();
      }}
      style={{ minWidth: '630px' }}
    >
      <Spin spinning={loading}>
        <div style={{ display: 'flex', marginBottom: '15px' }}>
          <span style={{ minWidth: '70px', lineHeight: '30px', marginRight: '1px' }}>模板名称</span>
          <Input
            placeholder={'请输入模板名称'}
            onChange={e => setMode(e.target.value)}
            defaultValue={mode}
            value={mode}
          />
        </div>
        <div style={{ display: 'flex' }}>
          <span style={{ minWidth: '70px', lineHeight: '30px', marginRight: '1px' }}>标题前缀</span>
          <Input
            value={tapdTitlePrefix}
            onChange={(e) => {
              setTapdTitlePrefix(e.target.value);
            }}
            placeholder={'请输入标题前缀'}
          />
        </div>
        <div style={{ marginBottom: '20px', marginTop: '30px', fontSize: '16px', fontWeight: 500, fontFamily: 'PingFang SC' }}>
          基本信息
        </div>
        {fields.map((item, index) => renderFields(item, index))}
        <div
          style={{
            marginLeft: '16px',
            color: '#0A6CFF',
            cursor: 'pointer',
            width: '80px',
          }}
        >
          <div onClick={() => {
            const filterFieldsList = customFields
              ?.filter((item: CustomFieldsType) => !fields.some(fields => fields.name === item.name));
            if (!filterFieldsList.length) {
              message.warning('该tapd项目下的字段名已全部添加');
              return;
            }
            setFields([...fields, filterFieldsList[0] ?? {}]);
          }}>
            <PlusCircleOutlined style={{ marginRight: '8px' }}/>
            添加字段
          </div>
        </div>
        <div style={{ textAlign: 'right', marginTop: '40px' }}>
          <Button onClick={() => {
            onHideEdit();
            openCreate();
          }}>
            取消
          </Button>
          <Button
            type='primary'
            onClick={async () => {
              await saveTapdInfo();
              mode && onHideEdit();
              mode && openCreate();
            }}
            style={{ marginLeft: '20px' }}>
            确定
          </Button>
        </div>
      </Spin>
    </Modal>
  );
};
